import { forwardRef, useId, useMemo, useState, type HTMLAttributes } from "react";
import { Button } from "./button.js";
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from "./select.js";
import { ToolCard } from "./tool-card.js";
import { cn } from "./lib/cn.js";

/**
 * Per-tool prompt override. Mirrors the `tool_overrides:` block in
 * `agents/<archetype>.yaml`:
 *
 * tool_overrides:
 * GAUSS: |
 * Your sustainable damage backbone. Lead with GAUSS every turn —
 * only +1 heat. **AMMO: 8 SHOTS TOTAL** — pick targets carefully;
 * ...
 * PPC: |
 * Knockout punch when target is at 4-18. ...
 *
 * Each override replaces the default `usage_hint` that the engine
 * renders into the system prompt's "Tool usage notes" section.
 * Empty / missing entries fall back to the catalog default
 * (`ToolSpec.usageHint`).
 */
export interface ToolOverrideRowProps
 extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
 /** Tool name (catalog key). */
 tool: string;
 /** Current override text. Empty string is allowed; the parent
 * decides whether empty means "remove" or "blank override". */
 value: string;
 onChange: (next: string) => void;
 /** Optional remove handler — when present, renders a × button. */
 onRemove?: () => void;
 rows?: number;
}

export const ToolOverrideRow = forwardRef<HTMLDivElement, ToolOverrideRowProps>(
 ({ tool, value, onChange, onRemove, rows = 4, className, ...rest }, ref) => {
 const id = useId();
 return (
 <div
 ref={ref}
 className={cn(
 "flex flex-col gap-2 border border-[var(--pp-border)] p-3",
 className,
 )}
 {...rest}
 >
 <div className="flex items-center gap-2">
 <ToolCard tool={tool} />
 <span className="ml-auto flex items-center gap-2">
 <label
 htmlFor={id}
 className="font-[var(--pp-font-family)] text-[var(--pp-font-sm)] uppercase tracking-[var(--pp-tracking-caps)] text-[var(--pp-text-muted)]"
 >
 Custom usage hint
 </label>
 {onRemove && (
 <Button
 variant="ghost"
 size="sm"
 onClick={onRemove}
 aria-label={`Remove ${tool} override`}
 >
 ✕
 </Button>
 )}
 </span>
 </div>
 <textarea
 id={id}
 value={value}
 onChange={(e) => onChange(e.currentTarget.value)}
 rows={rows}
 placeholder={`Override the default usage_hint for ${tool}. Empty = use the catalog default.`}
 spellCheck={false}
 className={cn(
 "w-full border border-[var(--pp-border)] bg-transparent p-2",
 "font-[var(--pp-font-family)] text-[var(--pp-font-md)] leading-relaxed text-[var(--pp-text)]",
 "placeholder:text-[var(--pp-text-muted)]",
 "focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 focus:ring-offset-[var(--background)]",
 )}
 />
 </div>
 );
 },
);
ToolOverrideRow.displayName = "ToolOverrideRow";

// ---------------------------------------------------------------------------
// ToolOverrideList
// ---------------------------------------------------------------------------

/** Map of tool name → override text. Empty / missing = use the
 * catalog default. Mirrors the YAML shape. */
export type ToolOverrides = Readonly<Record<string, string>>;

export interface ToolOverrideListProps
 extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
 /** The tools currently in the loadout — the pool the user can add
 * overrides for. Only tools in this list can have an override. */
 availableTools: readonly string[];
 /** Current map of overrides. */
 value: ToolOverrides;
 onChange: (next: ToolOverrides) => void;
}

export const ToolOverrideList = forwardRef<
 HTMLDivElement,
 ToolOverrideListProps
>(({ availableTools, value, onChange, className, ...rest }, ref) => {
 const [picker, setPicker] = useState<string>("");

 const present = useMemo(() => Object.keys(value), [value]);
 const addable = useMemo(
 () => availableTools.filter((t) => !(t in value)),
 [availableTools, value],
 );

 const setOverride = (tool: string, next: string) => {
 onChange({ ...value, [tool]: next });
 };

 const remove = (tool: string) => {
 const next = { ...value };
 delete next[tool];
 onChange(next);
 };

 const add = () => {
 if (!picker) return;
 if (picker in value) return;
 onChange({ ...value, [picker]: "" });
 setPicker("");
 };

 return (
 <div ref={ref} className={cn("flex flex-col gap-3", className)} {...rest}>
 <header className="flex items-baseline justify-between gap-3">
 <h3 className="font-[var(--pp-font-family)] text-[var(--pp-font-sm)] uppercase tracking-[var(--pp-tracking-caps)] text-[var(--pp-text-muted)]">
 Tool overrides
 </h3>
 <span className="font-[var(--pp-font-family)] text-[var(--pp-font-sm)] tabular-nums text-[var(--pp-text-muted)]">
 {present.length} / {availableTools.length}
 </span>
 </header>

 {present.length === 0 && (
 <p className=" border border-dashed border-[var(--pp-border)] p-4 text-center text-[var(--pp-font-md)] text-[var(--pp-text-muted)]">
 No per-tool overrides yet. Tools without an override use the
 catalog's default usage_hint.
 </p>
 )}

 {present.map((tool) => (
 <ToolOverrideRow
 key={tool}
 tool={tool}
 value={value[tool] ?? ""}
 onChange={(v) => setOverride(tool, v)}
 onRemove={() => remove(tool)}
 />
 ))}

 {addable.length > 0 && (
 <div className="flex items-center gap-2">
 <div className="flex-1">
 <Select value={picker} onValueChange={setPicker}>
 <SelectTrigger aria-label="Pick a tool to override">
 <SelectValue placeholder="Pick a loadout tool to customize…" />
 </SelectTrigger>
 <SelectContent>
 {addable.map((t) => (
 <SelectItem key={t} value={t}>
 {t}
 </SelectItem>
 ))}
 </SelectContent>
 </Select>
 </div>
 <Button onClick={add} disabled={!picker}>
 + Add override
 </Button>
 </div>
 )}
 </div>
 );
});
ToolOverrideList.displayName = "ToolOverrideList";
